import { AppError, ErrorCategory, ApiErrorResponse } from '@shared/types/errors';
import { HttpError } from '@shared/utils/HttpError';
import { ERROR_CODE_MESSAGES } from '@shared/i18n/translations/errorCodes';
import { isSessionExpired } from '@shared/utils/SessionExpiredError';

type ToastFunction = (message: string) => void;

interface ToastProvider {
  error: ToastFunction;
}

export class ErrorHandler {
  private static toastProvider: ToastProvider | null = null;

  static setToastProvider(provider: ToastProvider): void {
    this.toastProvider = provider;
  }

  static handle(error: unknown, context?: string, options: { notify?: boolean } = {}): AppError {
    const normalizedError = this.normalize(error, context);
    this.log(normalizedError);
    if (options.notify !== false) this.notify(normalizedError);
    return normalizedError;
  }

  private static normalize(error: unknown, context?: string): AppError {
    const timestamp = new Date();

    if (error instanceof HttpError) {
      return this.normalizeHttpError(error, context, timestamp);
    }

    if (this.isApiErrorResponse(error)) {
      return this.normalizeApiError(error, context, timestamp);
    }

    if (error instanceof Error) {
      return this.normalizeError(error, context, timestamp);
    }

    if (typeof error === 'string') {
      return {
        category: ErrorCategory.Unknown,
        message: error,
        context,
        timestamp,
        originalError: error,
      };
    }

    return {
      category: ErrorCategory.Unknown,
      message: 'errors.unknown',
      context,
      timestamp,
      originalError: error,
    };
  }

  private static isApiErrorResponse(error: unknown): error is ApiErrorResponse {
    return (
      typeof error === 'object' &&
      error !== null &&
      ('code' in error || 'message' in error || 'error' in error || 'status' in error || 'statusCode' in error)
    );
  }

  private static normalizeHttpError(
    error: HttpError,
    context: string | undefined,
    timestamp: Date
  ): AppError {
    const category = this.categorizeByStatusCode(error.status);
    const message = this.messageFor(error, category);

    return {
      category,
      message,
      code: error.code,
      statusCode: error.status,
      context,
      timestamp,
      originalError: error,
    };
  }

  private static messageFor(error: HttpError, category: ErrorCategory): string {
    if (error.code && Object.hasOwn(ERROR_CODE_MESSAGES, error.code)) {
      return `errors.codes.${error.code}`;
    }
    if (category === ErrorCategory.UpstreamUnavailable) {
      return 'errors.upstreamUnavailable';
    }
    return error.detail || error.message;
  }

  private static normalizeApiError(
    error: ApiErrorResponse,
    context: string | undefined,
    timestamp: Date
  ): AppError {
    const statusCode = error.status || error.statusCode;
    const message = error.message || error.error || 'errors.api.general';
    const category = this.categorizeByStatusCode(statusCode);

    return {
      category,
      message,
      code: error.code,
      statusCode,
      context,
      timestamp,
      originalError: error,
    };
  }

  private static normalizeError(
    error: Error,
    context: string | undefined,
    timestamp: Date
  ): AppError {
    const errorMessage = error.message.toLowerCase();

    if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
      return {
        category: ErrorCategory.Network,
        message: error.message,
        context,
        timestamp,
        originalError: error,
      };
    }

    return {
      category: ErrorCategory.Unknown,
      message: error.message,
      context,
      timestamp,
      originalError: error,
    };
  }

  private static categorizeByStatusCode(statusCode?: number): ErrorCategory {
    if (!statusCode) {
      return ErrorCategory.Unknown;
    }

    if (statusCode === 401) {
      return ErrorCategory.Authentication;
    }

    if (statusCode === 403) {
      return ErrorCategory.Authorization;
    }

    if (statusCode === 404) {
      return ErrorCategory.NotFound;
    }

    if (statusCode >= 400 && statusCode < 500) {
      return ErrorCategory.Validation;
    }

    if (statusCode === 502) {
      return ErrorCategory.UpstreamUnavailable;
    }

    if (statusCode >= 500) {
      return ErrorCategory.Server;
    }

    return ErrorCategory.Unknown;
  }

  private static notify(error: AppError): void {
    if (!this.toastProvider) {
      console.warn('Toast provider not configured. Error notification skipped.');
      return;
    }

    /**
     * An ended session is already handled: `unauthorizedReceived` resets the app and the screen
     * says so in words. A toast on top would repeat it — and once per call in flight, which is how
     * seven of them stacked up saying "No HATEOAS link found for root:subjects".
     *
     * Every failure that follows a failed discovery is that same session, wearing the name of
     * whichever link happened to be missing.
     */
    if (error.category === ErrorCategory.Authentication || isSessionExpired(error.message)) {
      return;
    }

    this.toastProvider.error(error.message);
  }

  private static log(error: AppError): void {
    const logMessage = [
      `[${error.category}]`,
      error.context ? `[${error.context}]` : '',
      error.code ? `[${error.code}]` : '',
      error.message,
      error.statusCode ? `(${error.statusCode})` : '',
    ]
      .filter(Boolean)
      .join(' ');

    console.error(logMessage, {
      error: error.originalError,
      timestamp: error.timestamp,
    });
  }
}
